import { useState } from "react"
import { API_ENDPOINTS } from '../../config/api'
import { Button } from '../ui/Button'
import { EnterpriseOperationalCard } from '../enterprise-operational/EnterpriseOperationalSection'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

type ProviderRow = {
  id: string
  providerKey: string
  displayName: string
  enabled: boolean
  healthStatus?: string
  apiKeyMasked?: string | null
  hasApiKey?: boolean
  baseUrl?: string | null
  model?: string | null
  embeddingModel?: string | null
  lastTestedAt?: string | null
  defaultForChat?: boolean
  defaultForEmbeddings?: boolean
  defaultForRag?: boolean
}

type TestResult = {
  success?: boolean
  chatModelAvailable?: boolean
  embeddingModelAvailable?: boolean
  latencyMs?: number
  errorMessage?: string | null
  testedAt?: string
}

type Props = {
  token: string | null
  provider: ProviderRow | null
  onSaved: () => void
}

const DEFAULT_BASE = 'https://api.openai.com/v1'
const DEFAULT_CHAT = 'gpt-4.1-mini'
const DEFAULT_EMBED = 'text-embedding-3-small'

export default function OpenAiProviderIntegrationCard({ token, provider, onSaved }: Props) {
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState(provider?.baseUrl || DEFAULT_BASE)
  const [chatModel, setChatModel] = useState(provider?.model || DEFAULT_CHAT)
  const [embeddingModel, setEmbeddingModel] = useState(provider?.embeddingModel || DEFAULT_EMBED)
  const [enabled, setEnabled] = useState(provider?.enabled ?? false)
  const [defaultForChat, setDefaultForChat] = useState(provider?.defaultForChat ?? true)
  const [defaultForEmbeddings, setDefaultForEmbeddings] = useState(provider?.defaultForEmbeddings ?? true)
  const [defaultForRag, setDefaultForRag] = useState(provider?.defaultForRag ?? true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<TestResult | null>(null)

  const headers = token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : undefined

  const save = async () => {
    if (!headers) return
    setSaving(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        providerKey: 'openai',
        displayName: 'OpenAI',
        baseUrl,
        model: chatModel,
        embeddingModel,
        enabled,
        defaultForChat,
        defaultForEmbeddings,
        defaultForRag,
        localMode: false,
        secureMode: true,
      }
      if (apiKey.trim()) body.apiKey = apiKey.trim()

      const url = provider?.id ? API_ENDPOINTS.AI_PROVIDER_BY_ID(provider.id) : API_ENDPOINTS.AI_PROVIDERS
      const method = provider?.id ? 'PATCH' : 'POST'
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || 'Failed to save OpenAI provider')
      setApiKey('')
      onSaved()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const testConnection = async () => {
    if (!headers || !provider?.id) {
      setError('Save the provider configuration before testing.')
      return
    }
    setTesting(true)
    setError(null)
    setTestResult(null)
    try {
      const res = await fetch(API_ENDPOINTS.AI_PROVIDER_TEST(provider.id), {
        method: 'POST',
        headers,
        body: '{}',
      })
      const json = await res.json().catch(() => ({}))
      const data = json?.data || {}
      setTestResult(data)
      if (!res.ok) throw new Error(data?.errorMessage || json?.message || 'Test failed')
      onSaved()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Test failed')
    } finally {
      setTesting(false)
    }
  }

  return (
    <EnterpriseOperationalCard title="OpenAI Provider">
      <p className={`${entStyles.metaRow} text-amber-800`} style={{ marginBottom: 8 }}>
        External AI will only receive data allowed by AI Data Policy. LOCAL_ONLY and NO_LLM content is never sent to
        OpenAI.
      </p>

      <form
        className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm"
        onSubmit={(e) => e.preventDefault()}
        autoComplete="off"
      >
        <label className="block">
          <span className="font-medium">API key</span>
          <input
            className="input w-full mt-1"
            type="password"
            autoComplete="off"
            placeholder={provider?.hasApiKey ? `Saved (${provider.apiKeyMasked || '****'})` : 'sk-…'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="font-medium">Base URL</span>
          <input className="input w-full mt-1" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
        </label>
        <label className="block">
          <span className="font-medium">Chat model</span>
          <input className="input w-full mt-1" value={chatModel} onChange={(e) => setChatModel(e.target.value)} />
        </label>
        <label className="block">
          <span className="font-medium">Embedding model</span>
          <input
            className="input w-full mt-1"
            value={embeddingModel}
            onChange={(e) => setEmbeddingModel(e.target.value)}
          />
        </label>
      </form>

      <div className="flex flex-wrap gap-4 mt-3 text-sm">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enable provider
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={defaultForChat} onChange={(e) => setDefaultForChat(e.target.checked)} />
          Default for chat
        </label>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={defaultForEmbeddings}
            onChange={(e) => setDefaultForEmbeddings(e.target.checked)}
          />
          Default for embeddings
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={defaultForRag} onChange={(e) => setDefaultForRag(e.target.checked)} />
          Default for RAG
        </label>
      </div>

      {provider ? (
        <p className={entStyles.metaRow} style={{ marginTop: 8 }}>
          <span>Status: {provider.healthStatus || 'unknown'}</span>
          {provider.lastTestedAt ? <span>Last tested: {new Date(provider.lastTestedAt).toLocaleString()}</span> : null}
        </p>
      ) : null}

      {testResult ? (
        <div className="text-sm mt-2 p-2 border border-slate-200 rounded bg-slate-50">
          <p className="m-0">Test: {testResult.success ? 'Success' : 'Failed'}</p>
          <p className="m-0">
            Chat: {testResult.chatModelAvailable ? 'OK' : 'No'} · Embeddings:{' '}
            {testResult.embeddingModelAvailable ? 'OK' : 'No'}
            {testResult.latencyMs != null ? ` · ${testResult.latencyMs}ms` : ''}
          </p>
          {testResult.errorMessage ? <p className="m-0 text-red-700">{testResult.errorMessage}</p> : null}
        </div>
      ) : null}

      {error ? (
        <p className="text-sm text-red-600 m-0 mt-2" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2 mt-3">
        <Button type="button" size="small" color="primary" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : provider ? 'Update OpenAI' : 'Save OpenAI'}
        </Button>
        {provider?.id ? (
          <Button type="button" size="small" color="outline" onClick={() => void testConnection()} disabled={testing}>
            {testing ? 'Testing…' : 'Test connection'}
          </Button>
        ) : null}
      </div>
    </EnterpriseOperationalCard>
  )
}
